import EventEmitter from 'eventemitter3';
import { FuseRenderer } from '@/renderer';
import { SHOT_PRESETS, randomTestShot } from '@/utils/shotPresets';
import { HandGestureTracker, SwingGuard, type SwingGuardOptions } from '@/xr/handInput';

interface EventMap {
  /** Quick select (hand pinch or controller trigger) — routed by the app:
   *  confirm anchoring, or fire a shot. */
  confirm: () => void;
  testShot: (shot: OpenGolfSim.Shot) => void;
  presetChanged: (index: number, name: string) => void;
  /**
   * B/Y pressed — open or close the action menu. Controller-only: a menu that
   * has to be pointed at is no use to a player with no ray, and hands have the
   * wrist panel instead.
   */
  menu: () => void;
  /** Select held (or controller squeeze held) — drop the anchor and re-place. */
  reanchor: () => void;
  /** Swing guard engaged/released — hand input is being ignored while true. */
  guardChanged: (active: boolean) => void;
}

/**
 * Selects shorter than this are a deliberate tap; longer holds are ignored
 * until they cross the re-anchor threshold, so a half-hearted grab does nothing.
 *
 * 1.0 s, not the 0.6 s this started at. Placing the tee is a careful gesture —
 * the player is looking down at a real ball and holding the pinch while they
 * check the reticle is where they want it — and past 0.6 s that landed in the
 * dead zone and silently did nothing. The 2 s re-anchor hold still has a full
 * second of daylight after this.
 */
const QUICK_SELECT_S = 1.0;
const HOLD_REANCHOR_S = 2.0;
const SQUEEZE_HOLD_S = 1.5;
/**
 * xr-standard gamepad mapping: 4 = A/X, 5 = B/Y (controllers only).
 * A/X steps the practice preset; B/Y is the menu.
 */
const BUTTON_PRIMARY = 4;
const BUTTON_SECONDARY = 5;

/**
 * Input-source slots to bind. Four, not two: Quest multimodal input reports
 * tracked hands AND held controllers at the same time, so a session can carry
 * four input sources. three assigns each new source to the first free slot among
 * the controllers that have been created, and **silently drops any source it has
 * no slot for** (WebXRManager: `if (controllerIndex === -1) break`, and
 * onSessionEvent returns early for unbound sources).
 *
 * With two slots the two hands can take both, leaving the controllers bound to
 * nothing: no select events, no face buttons, no grip pose. That looks exactly
 * like "the controller does nothing", which is what it was.
 */
const INPUT_SLOTS = 4;

type SelectState = { start: number; suppressedAtStart: boolean; reanchorFired: boolean };

export type XRShotControlsOptions = SwingGuardOptions & {
  /**
   * Practice preset selected at startup, named as in SHOT_PRESETS ('Chip',
   * 'Driver', …). Defaults to the first preset. Set here rather than by
   * reordering SHOT_PRESETS, which is public API and also maps the desktop
   * number keys 1-9 by array position.
   */
  initialPreset?: string;
};

/**
 * Input for the MR range. Hands and controllers are equal citizens — the player
 * can hit real balls with either in their pocket or in their hand:
 *
 *  hands                          controller
 *  - quick pinch                  - trigger tap        = confirm (place the
 *                                                        tee / set aim / fire
 *                                                        a practice shot)
 *  - pinch held 2 s               - trigger held 2 s
 *                                   or squeeze held    = re-anchor
 *  - thumb→middle pinch           - A/X                = cycle the preset
 *  - palm toward face (wrist)     - B/Y                = menu
 *
 * Every hand gesture is filtered through the SwingGuard so gripping and swinging
 * a real club can't trigger anything. Controllers skip the guard: they can't be
 * mistaken for a golf grip.
 */
export class XRShotControls extends EventEmitter<EventMap> {
  presetIndex = 0;
  readonly guard: SwingGuard;

  #inputSources: (XRInputSource | undefined)[] = [];
  #selects = new Map<number, SelectState>();
  #squeezeStart = new Map<number, number>();
  #squeezeFired = new Set<number>();
  #buttonsDown = new Map<string, boolean>();
  #hands = {
    left: new HandGestureTracker('left'),
    right: new HandGestureTracker('right'),
  };
  #middlePinchWas = { left: false, right: false };
  #guardWasActive = false;
  #elapsed = 0;

  constructor(renderer: FuseRenderer, options: XRShotControlsOptions = {}) {
    super();
    this.guard = new SwingGuard(options);

    if (options.initialPreset !== undefined) {
      const index = SHOT_PRESETS.findIndex(preset => preset.name === options.initialPreset);
      if (index >= 0) {
        this.presetIndex = index;
      } else {
        console.warn(`XRShotControls: unknown preset '${options.initialPreset}', keeping default`);
      }
    }

    const xr = renderer.xr;
    for (let i = 0; i < INPUT_SLOTS; i++) {
      const controller = xr.getController(i);
      controller.addEventListener('connected', (event: any) => {
        this.#inputSources[i] = event.data as XRInputSource;
      });
      controller.addEventListener('disconnected', () => {
        this.#inputSources[i] = undefined;
        this.#selects.delete(i);
        this.#squeezeStart.delete(i);
        this.#squeezeFired.delete(i);
        for (const key of [...this.#buttonsDown.keys()]) {
          if (key.startsWith(`${i}:`)) this.#buttonsDown.delete(key);
        }
      });
      controller.addEventListener('selectstart', () => this.#onSelectStart(i));
      controller.addEventListener('selectend', () => this.#onSelectEnd(i));
      controller.addEventListener('squeezestart', () => {
        this.#squeezeStart.set(i, this.#elapsed);
        this.#squeezeFired.delete(i);
      });
      controller.addEventListener('squeezeend', () => {
        this.#squeezeStart.delete(i);
      });
    }
  }

  get currentPreset(): { name: string; shot: OpenGolfSim.Shot } {
    if (this.presetIndex < 0) {
      return { name: 'Random', shot: randomTestShot() };
    }
    const preset = SHOT_PRESETS[this.presetIndex];
    return { name: preset.name, shot: { ...preset.shot } };
  }

  /** Any connected input source that is a tracked hand (vs a controller). */
  get hasHandInput(): boolean {
    return this.#inputSources.some(source => !!(source as any)?.hand);
  }

  /** The live gesture trackers (read-only consumers: wrist menu, debug). */
  get hands(): { left: HandGestureTracker; right: HandGestureTracker } {
    return this.#hands;
  }

  /**
   * Connected input sources, indexed by controller slot. Exposed so other XR
   * modules (controller rays, tee placement) can read grip/target-ray spaces
   * and gamepads without duplicating the connect/disconnect plumbing.
   */
  get inputSources(): readonly (XRInputSource | undefined)[] {
    return this.#inputSources;
  }

  /** Clear transient input state — call at session end so nothing (held
   *  squeezes, half-finished selects) leaks into the next session. */
  reset() {
    this.#selects.clear();
    this.#squeezeStart.clear();
    this.#squeezeFired.clear();
    this.#buttonsDown.clear();
    this.#middlePinchWas.left = false;
    this.#middlePinchWas.right = false;
  }

  /** Fire the current preset (the app calls this from 'confirm' when anchored). */
  fireCurrentPreset() {
    const preset = this.currentPreset;
    this.emit('testShot', preset.shot);
  }

  update(dt: number, frame?: XRFrame, referenceSpace?: XRReferenceSpace) {
    this.#elapsed += dt;

    // Hand joints → gesture state → swing guard
    if (frame && referenceSpace) {
      let sawLeft = false;
      let sawRight = false;
      for (const source of this.#inputSources) {
        const hand = (source as any)?.hand as XRHand | undefined;
        if (!hand) continue;
        if (source!.handedness === 'left') {
          this.#hands.left.update(frame, referenceSpace, hand, dt);
          sawLeft = true;
        } else if (source!.handedness === 'right') {
          this.#hands.right.update(frame, referenceSpace, hand, dt);
          sawRight = true;
        }
      }
      if (!sawLeft) this.#hands.left.update(frame, referenceSpace, undefined, dt);
      if (!sawRight) this.#hands.right.update(frame, referenceSpace, undefined, dt);
    }
    this.guard.update(dt, this.#hands.left, this.#hands.right);

    // Only meaningful when hands exist — with controllers-only, "all hands
    // suppressed" is vacuously true and must not read as "guard engaged"
    const guardActive = this.hasHandInput && this.guard.allSuppressed;
    if (guardActive !== this.#guardWasActive) {
      this.#guardWasActive = guardActive;
      this.emit('guardChanged', guardActive);
    }

    // Secondary gesture: thumb→middle pinch cycles the practice preset
    for (const handedness of ['left', 'right'] as const) {
      const tracker = this.#hands[handedness];
      const pinching = tracker.middlePinching && !this.guard.isSuppressed(handedness);
      if (pinching && !this.#middlePinchWas[handedness]) {
        this.cyclePreset(1);
      }
      this.#middlePinchWas[handedness] = pinching;
    }

    // Held-select → re-anchor (hands and controllers alike)
    for (const [index, state] of this.#selects) {
      if (state.reanchorFired) continue;
      if (this.#elapsed - state.start >= HOLD_REANCHOR_S && !this.#isSelectSuppressed(index)) {
        state.reanchorFired = true;
        this.emit('reanchor');
      }
    }

    // Controller squeeze-hold → re-anchor
    for (const [index, start] of this.#squeezeStart) {
      if (!this.#squeezeFired.has(index) && this.#elapsed - start >= SQUEEZE_HOLD_S) {
        this.#squeezeFired.add(index);
        this.emit('reanchor');
      }
    }

    // Controller face buttons: A/X steps the preset, B/Y is the menu
    for (let i = 0; i < this.#inputSources.length; i++) {
      const source = this.#inputSources[i];
      if (!source || (source as any).hand) continue;
      const gamepad = source.gamepad;
      if (!gamepad) continue;
      this.#checkButton(gamepad, i, BUTTON_PRIMARY, () => this.cyclePreset(1));
      this.#checkButton(gamepad, i, BUTTON_SECONDARY, () => this.emit('menu'));
    }
  }

  #onSelectStart(index: number) {
    this.#selects.set(index, {
      start: this.#elapsed,
      suppressedAtStart: this.#isSelectSuppressed(index),
      reanchorFired: false,
    });
  }

  #onSelectEnd(index: number) {
    const state = this.#selects.get(index);
    this.#selects.delete(index);
    if (!state || state.reanchorFired) return;
    // Judged on the START only. Testing the release too used to throw away good
    // pinches: letting a pinch go is the moment the fingertips come out of
    // occlusion and tracking is at its noisiest, and the guard's half-second
    // cooldown carries any blip in that window through to here. Within a window
    // this short a swing can't begin and finish, so the state at the start is
    // the honest gate.
    if (state.suppressedAtStart) return;
    const duration = this.#elapsed - state.start;
    if (duration <= QUICK_SELECT_S) {
      this.emit('confirm');
    }
    // 0.6–2 s: dead zone by design — hesitant grabs do nothing
  }

  /** Hand selects go through the guard; controllers can't hold a club. */
  #isSelectSuppressed(index: number): boolean {
    const source = this.#inputSources[index];
    if (!source) return true;
    if (!(source as any).hand) return false;
    return this.guard.isSuppressed(source.handedness);
  }

  #checkButton(gamepad: Gamepad, controllerIndex: number, buttonIndex: number, onPress: () => void) {
    const key = `${controllerIndex}:${buttonIndex}`;
    const pressed = !!gamepad.buttons[buttonIndex]?.pressed;
    const was = this.#buttonsDown.get(key) ?? false;
    if (pressed && !was) onPress();
    this.#buttonsDown.set(key, pressed);
  }

  /** Step the practice preset (±1). Public — the wrist menu arrows use it. */
  cyclePreset(direction: number) {
    // Cycle through presets plus the Random slot (-1)
    const count = SHOT_PRESETS.length + 1;
    const slot = (this.presetIndex + 1 + direction + count) % count; // shift so -1 → 0
    this.presetIndex = slot - 1;
    const name = this.presetIndex < 0 ? 'Random' : SHOT_PRESETS[this.presetIndex].name;
    this.emit('presetChanged', this.presetIndex, name);
  }
}
